import { useTranslation } from 'react-i18next'
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { ListItemCard } from 'src/components/layout/item-collection'
import { vendorTypeLabelMap, Vendor } from 'src/services/vendors/models'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { cn } from 'src/utils/style'

interface VendorItemProps {
  vendor: Vendor
  viewMode: ViewMode
  onEdit: (vendorId: string) => void
  onToggleActive: (vendorId: string, isActive: boolean) => void
  isTogglePending?: boolean
}

/** Table header for the list view; hidden below the `@3xl` container width. */
export const VendorListHeader = () => {
  const { t } = useTranslation()
  return (
    <div className="hidden rounded-lg bg-surface-hover px-6 py-3 text-sm font-medium text-text-secondary @3xl:flex">
      <div className="flex-1">{t('vendorsPage.tableHeaderName')}</div>
      <div className="@3xl:w-32 @3xl:shrink-0 @3xl:text-center">
        {t('vendorsPage.tableHeaderType')}
      </div>
      <div className="@3xl:w-40 @3xl:shrink-0 @3xl:text-center">
        {t('vendorsPage.tableHeaderTaxId')}
      </div>
      <div className="@3xl:w-24 @3xl:shrink-0 @3xl:text-center">
        {t('vendorsPage.tableHeaderStatus')}
      </div>
      <div className="@3xl:w-28 @3xl:shrink-0 @3xl:text-center">
        {t('vendorsPage.tableHeaderAction')}
      </div>
    </div>
  )
}

export const VendorItem = ({
  vendor,
  viewMode,
  onEdit,
  onToggleActive,
  isTogglePending,
}: VendorItemProps) => {
  const { t } = useTranslation()
  const isActive = vendor.isActive !== false
  const primaryName = vendor.displayName || vendor.name
  const secondaryName =
    vendor.displayName && vendor.displayName !== vendor.name ? vendor.name : null
  const typeLabel = vendorTypeLabelMap[vendor.vendorType] || vendor.vendorType
  const toggleLabel = isActive
    ? t('vendorsPage.deactivateVendor')
    : t('vendorsPage.reactivateVendor')
  const testIdSuffix = viewMode === 'card' ? '-card' : ''

  const statusBadge = (
    <span
      className={cn(
        'inline-block shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium',
        isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500',
      )}
    >
      {isActive ? t('common.active') : t('common.inactive')}
    </span>
  )

  const toggleButton = (
    <Button
      type="button"
      variant="ghost"
      tone={isActive ? 'neutral' : 'success'}
      iconOnly
      size="sm"
      shape="square"
      title={toggleLabel}
      aria-label={toggleLabel}
      data-testid={`vendor-toggle-active${testIdSuffix}-${vendor.id}`}
      onClick={(e) => {
        e.stopPropagation()
        onToggleActive(vendor.id, !isActive)
      }}
      disabled={isTogglePending}
    >
      {isActive ? <XCircleIcon className="h-5 w-5" /> : <CheckCircleIcon className="h-5 w-5" />}
    </Button>
  )

  if (viewMode === 'card') {
    return (
      <ListItemCard
        className={cn(!isActive && 'opacity-60')}
        data-testid={`vendor-card-${vendor.id}`}
        onClick={() => onEdit(vendor.id)}
      >
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0">
            <h3
              className={cn(
                'truncate text-lg font-bold',
                isActive ? 'text-text-primary' : 'text-text-tertiary',
              )}
              title={primaryName}
            >
              {primaryName}
            </h3>
            {secondaryName && (
              <p className="truncate text-sm text-text-placeholder" title={secondaryName}>
                {secondaryName}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {statusBadge}
            {toggleButton}
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('vendorsPage.tableHeaderType')}</dt>
            <dd className="truncate font-medium text-text-primary" title={typeLabel}>
              {typeLabel}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('vendorsPage.tableHeaderTaxId')}</dt>
            <dd
              className="truncate font-medium text-text-primary"
              title={vendor.taxId || undefined}
            >
              {vendor.taxId || '-'}
            </dd>
          </div>
        </dl>
      </ListItemCard>
    )
  }

  return (
    <div
      className={cn(
        'flex cursor-pointer flex-col gap-2 rounded-xl px-6 py-4 shadow transition-shadow hover:shadow-md @3xl:flex-row @3xl:items-center',
        isActive ? 'border border-border bg-surface' : 'border border-border bg-surface opacity-60',
      )}
      data-testid={`vendor-row-${vendor.id}`}
      onClick={() => onEdit(vendor.id)}
    >
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'break-words font-medium',
            isActive ? 'text-text-primary' : 'text-text-tertiary',
          )}
        >
          {primaryName}
        </p>
        {secondaryName && <p className="text-sm text-text-placeholder">{secondaryName}</p>}
      </div>
      <div className="text-sm text-text-secondary @3xl:w-32 @3xl:shrink-0 @3xl:text-center">
        <span className="font-medium text-text-placeholder @3xl:hidden">
          {t('vendorsPage.tableHeaderType')}:{' '}
        </span>
        {typeLabel}
      </div>
      <div className="text-sm text-text-secondary @3xl:w-40 @3xl:shrink-0 @3xl:text-center">
        <span className="font-medium text-text-placeholder @3xl:hidden">
          {t('vendorsPage.tableHeaderTaxId')}:{' '}
        </span>
        {vendor.taxId || '-'}
      </div>
      <div className="@3xl:w-24 @3xl:shrink-0 @3xl:text-center">{statusBadge}</div>
      <div className="flex items-center @3xl:w-28 @3xl:justify-center">{toggleButton}</div>
    </div>
  )
}
